import {
  Send,
  PenLine,
  Download,
  Route,
  ShieldAlert,
  CreditCard,
  History,
} from 'lucide-react';

/* ═══════════════════════════════════════════════════════════════
   ACTION QUEUE ITEMS — the ONE merged "needs you now" worklist.

   Pure. Builds the list the ActionQueue band renders: the SLA
   infringement rows from /api/infringements/action-queue plus
   synthetic obligation rows for downloads-overdue, unaccounted
   mileage, quarantine, driver-card renewals and the historic
   backlog. Sorted red-before-amber, most-overdue first.

   Lives here (not inside the component) so the Andre banner, its
   count and the "Start with Andre" hand-off read the SAME list the
   band beneath them renders — never a subset of it. A red download
   row must never sit under a green "nothing needs you" line.

   Item shape: { key, color, overdue, icon, title, sub, onClick,
                 date?, driver?, secondary? }
     key       — stable React key
     color     — 'red' | 'amber'
     overdue   — rank within the colour band (days, or a count)
     icon      — lucide component
     onClick   — deep-link via nav({ tab, ... }): the record
     date      — infringement rows only: the day, YYYY-MM-DD ('' if unknown)
     driver    — infringement rows only: the card-file name ('' if unknown)
     secondary — infringement rows with both: { label: 'Day', onClick } →
                 the calendar's Day view for that driver and date (design
                 2026-09-09-records §2: sentence → picture → record). The
                 band renders it as a quiet text link beside the primary
                 press; absent when either half is missing.

   actionQueue shape (server, snake_case; see server.py
   _action_queue_payload):
     { rows: [{ id, Date, driver_name, Type, Code, stage, color,
                overdue_by_days }],
       counts: { unsent_send_overdue, unsent_send_open,
                 sig_overdue, sig_open },
       historic: { count, before_date, oldest_date } }
   ═══════════════════════════════════════════════════════════════ */

/* ═══════════════════════════════════════════════════════════════
   DOWNLOADS OVERDUE — the two counts behind the red "N downloads
   overdue" row. This is FleetCommandBoard's fleetSummary derivation
   (overdueVU / overdueCards), lifted here unchanged so the Today tab
   builds its worklist from the SAME rule rather than a copy of it.

   A vehicle is overdue when its last download is more than 14 days
   old; a driver card when the driver's last activity is more than 28
   days old. A row with no date at all is overdue (never downloaded).
   An analogue truck is never overdue: see isAnalogueVehicle below.
   ═══════════════════════════════════════════════════════════════ */

export const VU_OVERDUE_AFTER_DAYS = 14;
export const CARD_OVERDUE_AFTER_DAYS = 28;

/**
 * Is this an analogue truck (design 2026-09-17 §8)? An analogue
 * tachograph has no vehicle unit to download: the truck's record is the
 * paper disc. So a missing or old download date on it is not "overdue".
 * Nothing could ever clear it, and the truck would read red for ever.
 *
 * The server says so on the lock-status row: tachograph_type 'analogue',
 * and download_rag / recency_rag 'analogue' (so a reader that only knows
 * the colour stops chasing too). The rows built in this file carry it on
 * as tachographType. Only these words count: a digital truck, or one
 * whose type is unknown, is chased exactly as before. False on anything
 * that is not a row, never a throw.
 */
export function isAnalogueVehicle(v) {
  if (!v || typeof v !== 'object') return false;
  return v.tachograph_type === 'analogue'
    || v.tachographType === 'analogue'
    || v.download_rag === 'analogue'
    || v.recency_rag === 'analogue';
}

/* An analogue truck's row takes its type along, so downloadsOverdue can
   leave it out further down the line. Any other row keeps exactly the
   shape it always had. */
function withAnalogueType(row, source) {
  return isAnalogueVehicle(source) ? { ...row, tachographType: 'analogue' } : row;
}

/* Whole days since an ISO date/timestamp; 999 (= "never") when blank or
   unparseable. Mirrors FleetCommandBoard's local daysSince exactly. */
export function daysSince(dateStr, now = new Date()) {
  if (!dateStr) return 999;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return 999;
  return Math.floor((now - d) / 86400000);
}

/* The driver-row field the board reads as "last seen", in its order. */
export function driverLastSeen(d) {
  if (!d) return '';
  return d.last_activity_date || d.LastActivityDate || d.upload_timestamp || '';
}

/* Driver-name key: trim + collapse inner whitespace + uppercase — so a
   card spelling ('JONATHON GREENWOOD') and a roster name meet in one set. */
export const normDriverName = (s) =>
  String(s || '').trim().replace(/\s+/g, ' ').toUpperCase();

/* The CURRENT workforce, from /api/driver-status-grid: every active,
   non-dormant roster row under its roster name AND every card spelling
   it owns (file_identities). Archived and dormant rows never count, whatever
   the grid's include-archived toggle showed. null until the grid has loaded
   (no grid is not an empty workforce). Fleet command's Last Card Reads and
   both overdue counts use this one set, so a headline never counts a name
   its list leaves out. */
export function currentWorkforceNames(grid) {
  if (!grid || !Array.isArray(grid.drivers)) return null;
  const set = new Set();
  for (const d of grid.drivers) {
    if (!d || d.dormant || d.status === 'archived') continue;
    for (const n of [d.driver_name, ...(Array.isArray(d.file_identities) ? d.file_identities : [])]) {
      const key = normDriverName(n);
      if (key) set.add(key);
    }
  }
  return set;
}

/**
 * Lock-status rows (GET /api/vehicles/lock-status) as the minimal vehicle
 * shape downloadsOverdue reads: the day part of `last_download_date`, which
 * is the date the board seeds its vehicle grid with when lock-status data is
 * present. Null when the unit has never reported a download.
 */
export function vehiclesFromLockStatus(lockStatusData) {
  const rows = Array.isArray(lockStatusData) ? lockStatusData : [];
  return rows
    .filter((lv) => lv && typeof lv === 'object')
    .map((lv) => withAnalogueType({
      registration: lv.registration,
      lastDate: lv.last_download_date ? String(lv.last_download_date).slice(0, 10) : null,
    }, lv));
}

/* Registration spellings vary by embedded spacing across sources
   ('LJ22 EYT' vs 'LJ22EYT' — same physical truck). Mirrors
   FleetCommandBoard's normVrn and api/server.py _vrn_key. */
export const normVrn = (v) => String(v || '').replace(/\s+/g, '').toUpperCase();

/**
 * FleetCommandBoard's vehicle-list derivation (its `vehicles` memo, minus
 * the per-vehicle rendering enrichment), lifted here UNCHANGED so the Today
 * tab counts downloads overdue over the SAME vehicles, with the SAME
 * last-date rule, as the board's fleetSummary does.
 *
 * The rule the board carries, which a plain lock-status read gets wrong:
 *   • a lock-status row with no `last_download_date` falls back to the
 *     latest driver-activity / mileage date seen on that vehicle — a truck
 *     driven yesterday is not "never downloaded";
 *   • when lock-status is empty the vehicles are the ones the driver /
 *     mileage / fleet rows name, not an empty list.
 *
 * Returns the board's intermediate rows: { registration, drivers (Set),
 * lastDate, infringementCount, activityCount, hasNoCard, missingKm,
 * missingCount, vin? }. Each input tolerates null / non-array.
 */
export function buildVehicleList({
  lockStatusData,
  drivers,
  infringements,
  mileageData,
  fleetVehicles,
} = {}) {
  const driverList = Array.isArray(drivers) ? drivers : [];
  const infList = Array.isArray(infringements) ? infringements : [];
  const mileage = Array.isArray(mileageData) ? mileageData : [];
  const lockRows = Array.isArray(lockStatusData) ? lockStatusData : [];

  const vehicleMap = {};

  // Seed the grid with the FULL fleet from the source-of-truth
  // vehicle list (derived server-side from activities when the
  // curated `vehicles` table is empty). Root-caused 2026-05-19 on
  // Bannister: previously the grid was built ONLY from mileageData,
  // so a 55-vehicle fleet showed just the 3 vehicles that happened
  // to have mileage discrepancies. Mileage/infringement/lock data
  // below now enriches these seeded entries.
  // vehicleMap is keyed by NORMALISED registration (normVrn); each entry
  // keeps the first-seen RAW spelling in .registration for display.
  const seedVehicles = Array.isArray(fleetVehicles) ? fleetVehicles : [];
  for (const v of seedVehicles) {
    const reg = v.registration || v.VehicleRegistration || v.reg || '';
    const key = normVrn(reg);
    if (!key || vehicleMap[key]) continue;
    vehicleMap[key] = {
      registration: reg,
      vin: v.vin || null,
      drivers: new Set(),
      lastDate: null,
      infringementCount: 0,
      activityCount: 0,
      hasNoCard: false,
    };
  }

  for (const row of mileage) {
    const reg = row.VehicleRegistration || row.vehicle_registration || row.Vehicle || '';
    const key = normVrn(reg);
    if (!key) continue;
    if (!vehicleMap[key]) {
      vehicleMap[key] = {
        registration: reg,
        drivers: new Set(),
        lastDate: null,
        infringementCount: 0,
        activityCount: 0,
        hasNoCard: false,
      };
    }
    const driver = row.DriverName || row.driver_name || row.Driver || '';
    if (driver) vehicleMap[key].drivers.add(driver);
    const date = row.Date || row.date || row.ActivityDate || '';
    if (date && (!vehicleMap[key].lastDate || date > vehicleMap[key].lastDate)) {
      vehicleMap[key].lastDate = date;
    }
    vehicleMap[key].activityCount++;
    const disc = parseFloat(row.Discrepancy || row.discrepancy || 0);
    if (disc > 0) {
      vehicleMap[key].hasNoCard = true;
      vehicleMap[key].missingKm = (vehicleMap[key].missingKm || 0) + disc;
      vehicleMap[key].missingCount = (vehicleMap[key].missingCount || 0) + 1;
    }
  }

  for (const inf of infList) {
    const key = normVrn(inf.VehicleRegistration || inf.vehicle_registration || inf.Vehicle || '');
    if (key && vehicleMap[key]) {
      vehicleMap[key].infringementCount++;
    }
  }

  for (const d of driverList) {
    const reg = d.VehicleRegistration || d.vehicle_registration || d.Vehicle || '';
    const key = normVrn(reg);
    if (!key) continue;
    if (!vehicleMap[key]) {
      vehicleMap[key] = {
        registration: reg,
        drivers: new Set(),
        lastDate: null,
        infringementCount: 0,
        activityCount: 0,
        hasNoCard: false,
      };
    }
    const name = d.driver_name || d.DriverName || d.name || '';
    if (name) vehicleMap[key].drivers.add(name);
    const date = d.last_activity_date || d.LastActivityDate || '';
    if (date && (!vehicleMap[key].lastDate || date > vehicleMap[key].lastDate)) {
      vehicleMap[key].lastDate = date;
    }
  }

  // Filter out placeholder vehicles
  delete vehicleMap[normVrn('External Vehicle')];
  delete vehicleMap[''];
  delete vehicleMap[normVrn('undefined')];

  // If we have registered fleet vehicles (from lock-status API), only show those
  // This filters out external vehicles that drivers used but aren't in the org's fleet
  const fleetRegs = new Set(lockRows.map(lv => normVrn(lv.registration)).filter(Boolean));
  let vehicleList;
  if (fleetRegs.size > 0) {
    // Start with fleet vehicles, add data from vehicleMap if available
    vehicleList = lockRows.map(lv => {
      const existing = vehicleMap[normVrn(lv.registration)] || {};
      return withAnalogueType({
        registration: lv.registration,
        drivers: existing.drivers || new Set(),
        lastDate: lv.last_download_date ? lv.last_download_date.slice(0, 10) : existing.lastDate,
        infringementCount: existing.infringementCount || 0,
        activityCount: existing.activityCount || 0,
        hasNoCard: existing.hasNoCard || false,
        missingKm: existing.missingKm || 0,
        missingCount: existing.missingCount || 0,
      }, lv);
    });
  } else {
    vehicleList = Object.values(vehicleMap);
  }
  return vehicleList;
}

/**
 * The vehicles downloadsOverdue counts, as FleetCommandBoard feeds them:
 * buildVehicleList's rows with the board's `dataAge` (whole days since
 * lastDate, 999 = never). Today calls this with the feeds it has (lock
 * status + drivers); the board's fleetSummary reaches the same numbers
 * through its `vehicles` memo, which starts from the same function.
 */
export function vehiclesForOverdue({
  lockStatusData,
  drivers,
  infringements,
  mileageData,
  fleetVehicles,
  now = new Date(),
} = {}) {
  return buildVehicleList({ lockStatusData, drivers, infringements, mileageData, fleetVehicles })
    .map((v) => withAnalogueType({
      registration: v.registration,
      lastDate: v.lastDate,
      dataAge: daysSince(v.lastDate, now),
    }, v));
}

/**
 * @param {object} args
 * @param {Array}  [args.vehicles]  rows with `dataAge` (days, precomputed by
 *                                  the board's grid) or a `lastDate`
 * @param {Array}  [args.drivers]   /api/drivers rows
 * @param {Set}    [args.workforce] currentWorkforceNames(grid): when given,
 *                                  only these drivers' cards can be overdue —
 *                                  /api/drivers lists every name that ever
 *                                  uploaded a card, leavers included
 * @param {Date}   [args.now]
 * @returns {{ vuOverdue: number, cardOverdue: number }}
 */
export function downloadsOverdue({ vehicles, drivers, workforce, now = new Date() } = {}) {
  const vList = Array.isArray(vehicles) ? vehicles : [];
  const dList = (Array.isArray(drivers) ? drivers : []).filter(
    (d) => !(workforce instanceof Set)
      || workforce.has(normDriverName(d && (d.driver_name || d.DriverName || d.name))),
  );
  const vuOverdue = vList.filter((v) => {
    // No unit to download, so never late: the paper disc is the record.
    if (isAnalogueVehicle(v)) return false;
    const age = v && typeof v.dataAge === 'number' ? v.dataAge : daysSince(v ? v.lastDate : '', now);
    return age > VU_OVERDUE_AFTER_DAYS;
  }).length;
  const cardOverdue = dList.filter(
    (d) => daysSince(driverLastSeen(d), now) > CARD_OVERDUE_AFTER_DAYS,
  ).length;
  return { vuOverdue, cardOverdue };
}

// red sorts before amber; unknown colours sink to the bottom.
const COLOR_RANK = { red: 0, amber: 1, green: 2 };

/* Human SLA / clock line for an infringement action-queue row. */
export function infringementSla(row) {
  const over = row.overdue_by_days;
  if (row.stage === 'unsent_send') {
    if (row.color === 'red') {
      return `${over} day${over === 1 ? '' : 's'} late to send`;
    }
    return 'Send within 7 days of the day it happened';
  }
  if (row.stage === 'sent_sig') {
    if (row.color === 'red') {
      return `Signature ${over} day${over === 1 ? '' : 's'} late`;
    }
    return 'Waiting for the driver to sign';
  }
  if (row.stage === 'held_review') {
    // WR-COMP held for manager review (2026-10-04): never sent, no SLA.
    return 'Held for manager review, not sent to the driver';
  }
  return 'Needs review';
}

function infringementVerb(stage) {
  if (stage === 'unsent_send') return 'Note to send';
  if (stage === 'held_review') return 'Held for review';
  return 'Signature to chase';
}

/* The day part of an action-queue row's Date (YYYY-MM-DD); '' when absent. */
export function infringementDay(row) {
  const raw = row && row.Date != null ? String(row.Date) : '';
  return raw.slice(0, 10);
}

export function infringementTitle(row) {
  const who = (row.driver_name || 'Unknown driver').trim() || 'Unknown driver';
  const verb = infringementVerb(row.stage);
  return `${verb}: ${infringementWhat(row, who)}`;
}

/* What happened, as one sentence that names the driver (UX pass
   2026-10-16, C5): the story headline (server, Task 5) when present,
   with "The driver" (the roster had no name for the card) replaced by
   the name, so the manager always knows WHO. The (kind) form is the fallback before the backend lands. */
export function infringementWhat(row, whoArg) {
  const who = whoArg || (row.driver_name || 'Unknown driver').trim() || 'Unknown driver';
  const headline = typeof row.headline === 'string' ? row.headline.trim() : '';
  if (/^The driver\b/.test(headline)) return `${who}${headline.slice('The driver'.length)}`;
  if (headline) return headline;
  const kind = row.Type || '';
  return kind ? `${who} (${kind})` : who;
}

/**
 * Build the merged, worst-first worklist.
 *
 * @param {object} args
 * @param {object} [args.actionQueue]      server payload (rows, counts, historic)
 * @param {object} [args.downloads]        { vuOverdue, cardOverdue }
 * @param {number} [args.mileageKm]        unaccounted km (mileageSummary.total_km)
 * @param {number} [args.mileageVehicles]  vehicles with a gap
 * @param {number} [args.quarantinePending]
 * @param {object} [args.cardExpiry]       /api/card-expiry-watch payload
 * @param {function} [args.nav]            deep-link navigator; no-op when absent
 */
export function buildActionQueueItems({
  actionQueue,
  downloads,
  mileageKm,
  mileageVehicles,
  quarantinePending,
  cardExpiry,
  nav: navArg,
} = {}) {
  const nav = typeof navArg === 'function' ? navArg : () => {};

  const infRows = Array.isArray(actionQueue?.rows) ? actionQueue.rows : [];

  // Infringement worklist items (already worst-first from the server, but we
  // re-sort the merged list below so synthetic rows interleave correctly).
  const items = infRows.map((row) => {
    const date = infringementDay(row);
    const driver = typeof row.driver_name === 'string' ? row.driver_name : '';
    const item = {
      key: `inf-${row.id}`,
      color: row.color,
      overdue: row.overdue_by_days || 0,
      icon: row.stage === 'unsent_send' ? Send
        : row.stage === 'held_review' ? ShieldAlert : PenLine,
      title: infringementTitle(row),
      what: infringementWhat(row),
      next: infringementVerb(row.stage),
      sub: infringementSla(row),
      date,
      driver,
      onClick: () => nav({ tab: 'infringements', infFilter: { ids: [row.id] } }),
    };
    // The day (the picture) is a second, quieter door: only when the row
    // names both the card-file driver the calendar filters on and the date.
    // focus: '' so a stale vehicle focus never lands the Day view in
    // vehicle mode (parity with the Infringements tab's calendar link).
    if (date && driver) {
      item.secondary = {
        label: 'View day',
        onClick: () => nav({ tab: 'calendar', driver, date, focus: '' }),
      };
    }
    return item;
  });

  // ── Synthetic obligation rows ──────────────────────────────────
  const vuOverdue = downloads?.vuOverdue || 0;
  const cardOverdue = downloads?.cardOverdue || 0;
  const downloadTotal = vuOverdue + cardOverdue;
  if (downloadTotal > 0) {
    items.push({
      key: 'downloads-overdue',
      color: 'red',
      overdue: downloadTotal, // rank by size within the red band
      icon: Download,
      next: 'Arrange downloads',
      title: `${downloadTotal} download${downloadTotal !== 1 ? 's' : ''} overdue`,
      sub: `${vuOverdue} truck${vuOverdue === 1 ? '' : 's'} · ${cardOverdue} card${cardOverdue === 1 ? '' : 's'} not downloaded`,
      // People & vehicles (design 2026-09-08 §3): the trucks are the subject,
      // so the row opens the Vehicles list, where each truck says why.
      onClick: () => nav({ tab: 'people', section: 'vehicles' }),
    });
  }

  const km = Math.round(mileageKm || 0);
  const mVeh = mileageVehicles || 0;
  if (km > 0) {
    items.push({
      key: 'unaccounted-mileage',
      color: 'amber',
      overdue: 0,
      icon: Route,
      next: 'Investigate',
      title: `${km} km with no card`,
      sub: mVeh > 0
        ? `${mVeh} vehicle${mVeh !== 1 ? 's' : ''}`
        : 'Miles the truck recorded that no card accounts for',
      onClick: () => nav({ tab: 'mileage' }),
    });
  }

  const qPending = quarantinePending || 0;
  if (qPending > 0) {
    items.push({
      key: 'quarantine',
      color: 'amber',
      overdue: 0,
      icon: ShieldAlert,
      next: 'Fix and upload again',
      title: `${qPending} file${qPending !== 1 ? 's' : ''} we couldn't place`,
      sub: 'Not in the analysis yet',
      onClick: () => nav({ tab: 'dataCentre' }),
    });
  }

  // Driver-card renewals due (/api/card-expiry-watch). Expired cards are a
  // red obligation (the driver can't legally drive); cards inside the 30-day
  // window are an amber heads-up. One combined row, red when anything has
  // already expired. Guarded — the feed fails soft to zero counts.
  const cardExpired = cardExpiry?.counts?.expired || 0;
  const cardExpiringSoon = cardExpiry?.counts?.expiring_soon || 0;
  if (cardExpired > 0 || cardExpiringSoon > 0) {
    const cardColor = cardExpired > 0 ? 'red' : 'amber';
    const cardTitle = cardExpired > 0
      ? `${cardExpired} driver card${cardExpired !== 1 ? 's' : ''} expired`
      : `${cardExpiringSoon} driver card${cardExpiringSoon !== 1 ? 's' : ''} expiring`;
    const cardSub = cardExpired > 0
      ? (cardExpiringSoon > 0
          ? `${cardExpiringSoon} more within 30 days · renew before driving`
          : 'Cannot legally drive · renew now')
      : 'Within 30 days';
    items.push({
      key: 'card-expiry',
      color: cardColor,
      overdue: cardExpired, // rank by expired count within the red band
      icon: CreditCard,
      next: 'Arrange renewal',
      title: cardTitle,
      sub: cardSub,
      onClick: () => nav({ tab: 'dataCentre' }),
    });
  }

  // Historic backlog (>90d) — collapsed into ONE amber row rather than a
  // wall of individual overdue letters. Server aggregates these in
  // _action_queue_payload (historic: { count, before_date, oldest_date }).
  // Clicking opens the Infringements tab with a durable close-all intent.
  const historicCount = actionQueue?.historic?.count || 0;
  const historicBeforeDate = actionQueue?.historic?.before_date || '';
  if (historicCount > 0) {
    items.push({
      key: 'historic-infringements',
      color: 'amber',
      overdue: 0,
      icon: History,
      next: 'Review',
      title: `${historicCount} historic infringement${historicCount !== 1 ? 's' : ''}`,
      sub: `Before ${historicBeforeDate}`,
      onClick: () => nav({
        tab: 'infringements',
        historicClose: { before_date: historicBeforeDate, count: historicCount },
      }),
    });
  }

  // Worst-first across the whole merged list: red before amber, then the
  // most-overdue (or largest) within each colour band.
  items.sort((a, b) => {
    const ra = COLOR_RANK[a.color] ?? 3;
    const rb = COLOR_RANK[b.color] ?? 3;
    if (ra !== rb) return ra - rb;
    return (b.overdue || 0) - (a.overdue || 0);
  });

  return items;
}
